import { ReadonlyMat4, ReadonlyVec3, mat4, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';

export interface ResampleResult<T extends Int16Array | Uint8Array> {
    pixels: T;
    dstGrid: Grid;
    /**
     * QA matrix: maps derived voxel indices [i',j',k',1] -> original voxel indices [i,j,k,1]
     * v_src = qaVoxelDstToSrc * v_dst
     */
    qaVoxelDstToSrc: mat4;
    undefinedSamples: number;
}

export interface ArbitraryResampleOptions {
    /**
     * Matrix in *patient space* mapping destination world coords to source world coords:
     *
     *   p_src = patientSrcFromDst * p_dst
     *
     * If omitted, identity is used (dst patient == src patient).
     */
    patientSrcFromDst?: ReadonlyMat4;

    /**
     * Value to use outside source volume bounds.
     * For Int16 CT/MR this is often 0; for Uint8 SDF/masks maybe 0 or 255.
     */
    backgroundValue?: number;

    /**
     * Whether to use fast 8-voxel average instead of full trilinear.
     * (Keep false if you care about exact interpolation, e.g. SDF.)
     */
    fastAverageInsteadOfTrilinear?: boolean;

    /**
     * If true, clamp out-of-bounds sample positions to the
     * nearest edge voxel instead of returning backgroundValue.
     * Produces a natural edge-extension effect.
     *
     * Either way the boundary is the source's VOXEL EXTENT — half a voxel
     * beyond the outermost voxel centres — so `false` pads everything the
     * source's voxels do not cover, and nothing they do.
     */
    clampToEdge?: boolean;

    /** Optional clamp range; if omitted, derived from output typed array type. */
    clampMin?: number;
    clampMax?: number;

    patientSrcFromDstFn?: (dstWorld: ReadonlyVec3) => vec3 | undefined;
}

/**
 * Build voxelDst -> voxelSrc mapping for arbitrary src/dst grids
 * (and optional patient-space registration).
 *
 * Both grids' `voxelToWorld` place index i at the LOW CORNER of voxel i —
 * `Grid.fromDicom` subtracts half a voxel from ImagePositionPatient to get
 * there. A voxel's value, though, belongs at its CENTRE, which is where
 * the writer puts it back (`buildDerivedSeriesFromVolume` adds the half
 * voxel again). Mapping corner to corner therefore samples the source half
 * a source voxel early and delivers the result half a destination voxel
 * late, leaving the output shifted by (dstRes - srcRes) / 2 per axis.
 *
 * That cancels exactly when the two resolutions match, so an identity
 * resample is pixel-exact either way; it is worst when the resolution
 * changes, which is what a reformat is for — a 1 mm series reformatted to
 * 3 mm slices moves by 1 mm.
 *
 * So the mapping is built centre to centre: shift into centres before
 * applying the grid transforms, and back out of them afterwards.
 */
export function buildQaVoxelDstToSrc(
    srcGrid: Grid,
    dstGrid: Grid,
    patientSrcFromDst?: ReadonlyMat4
): mat4 {
    // src: voxel -> world
    const T_src_phys = srcGrid.voxelToWorld as mat4;
    // dst: voxel -> world
    const T_dst_phys = dstGrid.voxelToWorld as mat4;

    const invSrc = mat4.create();
    if (!mat4.invert(invSrc, T_src_phys)) {
        throw new Error('Source voxelToWorld transform is not invertible.');
    }

    // patientSrcFromDst is world_dst -> world_src
    const M_patientSrc_from_patientDst = patientSrcFromDst
        ? (patientSrcFromDst as mat4)
        : mat4.create();

    if (!patientSrcFromDst) {
        mat4.identity(M_patientSrc_from_patientDst);
    }

    // qaVoxelDstToSrc = inv(T_src_phys) * M_patientSrc_from_patientDst * T_dst_phys
    const qaVoxelDstToSrc = mat4.create();
    const tmp = mat4.create();
    mat4.multiply(tmp, M_patientSrc_from_patientDst, T_dst_phys);
    mat4.multiply(qaVoxelDstToSrc, invSrc, tmp);

    // Index i -> centre of voxel i on the way in, centre -> index on the
    // way out, so the corner-based grid transforms above are evaluated
    // between the points the voxel values actually describe.
    const toCenter = mat4.fromTranslation(mat4.create(), [0.5, 0.5, 0.5]);
    const fromCenter = mat4.fromTranslation(mat4.create(), [-0.5, -0.5, -0.5]);
    mat4.multiply(qaVoxelDstToSrc, qaVoxelDstToSrc, toCenter);
    mat4.multiply(qaVoxelDstToSrc, fromCenter, qaVoxelDstToSrc);

    return qaVoxelDstToSrc;
}
export function resampleGridCPU<
    TIn extends Int16Array | Uint8Array,
    TOut extends Int16Array | Uint8Array,
>(
    inputPixels: TIn,
    srcGrid: Grid,
    dstGrid: Grid,
    outputCtor: { new (length: number): TOut },
    options: ArbitraryResampleOptions = {}
): ResampleResult<TOut> {
    const srcNx = srcGrid.sizeX | 0;
    const srcNy = srcGrid.sizeY | 0;
    const srcNz = srcGrid.sizeZ | 0;

    if (inputPixels.length !== srcNx * srcNy * srcNz) {
        throw new Error('Input pixel data length does not match srcGrid size.');
    }

    const dstNx = dstGrid.sizeX | 0;
    const dstNy = dstGrid.sizeY | 0;
    const dstNz = dstGrid.sizeZ | 0;

    // Build voxelDst -> voxelSrc mapping
    const qaVoxelDstToSrc = buildQaVoxelDstToSrc(
        srcGrid,
        dstGrid,
        options.patientSrcFromDst
    );
    const M = qaVoxelDstToSrc;

    // Decompose into “origin + 3 steps” form for efficient iteration
    const c0 = vec3.fromValues(M[0], M[1], M[2]); // step when i++
    const c1 = vec3.fromValues(M[4], M[5], M[6]); // step when j++
    const c2 = vec3.fromValues(M[8], M[9], M[10]); // step when k++
    const c3 = vec3.fromValues(M[12], M[13], M[14]); // src voxel for [0,0,0,1]

    const baseZ = vec3.create();
    const rowStart = vec3.create();
    const srcPos = vec3.create();

    const outPixels = new outputCtor(dstNx * dstNy * dstNz);

    const backgroundValue = options.backgroundValue ?? 0;

    // Determine clamp range from options or typed array type
    let clampMin: number;
    let clampMax: number;

    if (options.clampMin !== undefined && options.clampMax !== undefined) {
        clampMin = options.clampMin;
        clampMax = options.clampMax;
    } else if (outPixels instanceof Int16Array) {
        clampMin = -32768;
        clampMax = 32767;
    } else {
        // Uint8
        clampMin = 0;
        clampMax = 255;
    }

    const fastAverage = !!options.fastAverageInsteadOfTrilinear;
    const clampToEdge = !!options.clampToEdge;

    const sampleTrilinear = (x: number, y: number, z: number): number => {
        let x0 = Math.floor(x);
        let y0 = Math.floor(y);
        let z0 = Math.floor(z);
        const x1 = x0 + 1;
        const y1 = y0 + 1;
        const z1 = z0 + 1;

        // Outside the source's VOXEL EXTENT, which reaches half a voxel
        // past the outermost voxel centres: an index names the CENTRE of
        // its voxel, so the volume occupies [-0.5, srcN - 0.5] on each
        // axis, not [0, srcN - 1].
        //
        // Tested here rather than on the index range below, because the
        // two differ by exactly the outer half-voxel and that shell fell
        // through to the interpolation, where both corner indices clamp
        // to the same edge voxel and returned its value. That is edge
        // extension, produced under the option that asks for the
        // opposite: half a voxel of the source's border was smeared
        // across the shell `paddedFractionInBox` reports as fabricated,
        // and the seam the padding value is chosen to hide sat half a
        // voxel outside the boundary instead of on it.
        if (
            !clampToEdge &&
            (x < -0.5 ||
                y < -0.5 ||
                z < -0.5 ||
                x > srcNx - 0.5 ||
                y > srcNy - 0.5 ||
                z > srcNz - 0.5)
        ) {
            return backgroundValue;
        }

        // Completely outside the index range, edge extension asked for:
        // the nearest edge voxel. Unreachable when `clampToEdge` is
        // false, the test above having already returned.
        if (
            x1 < 0 ||
            y1 < 0 ||
            z1 < 0 ||
            x0 >= srcNx ||
            y0 >= srcNy ||
            z0 >= srcNz
        ) {
            // Edge-extend: clamp to nearest edge voxel
            const ci = Math.max(0, Math.min(srcNx - 1, Math.round(x)));
            const cj = Math.max(0, Math.min(srcNy - 1, Math.round(y)));
            const ck = Math.max(0, Math.min(srcNz - 1, Math.round(z)));
            return inputPixels[ci + cj * srcNx + ck * srcNx * srcNy];
        }

        if (x0 < 0) {
            x0 = 0;
        }
        if (y0 < 0) {
            y0 = 0;
        }
        if (z0 < 0) {
            z0 = 0;
        }
        const x0c = x0;
        const y0c = y0;
        const z0c = z0;
        const x1c = x1 >= srcNx ? srcNx - 1 : x1;
        const y1c = y1 >= srcNy ? srcNy - 1 : y1;
        const z1c = z1 >= srcNz ? srcNz - 1 : z1;

        const idx000 = x0c + y0c * srcNx + z0c * srcNx * srcNy;
        const idx100 = x1c + y0c * srcNx + z0c * srcNx * srcNy;
        const idx010 = x0c + y1c * srcNx + z0c * srcNx * srcNy;
        const idx110 = x1c + y1c * srcNx + z0c * srcNx * srcNy;
        const idx001 = x0c + y0c * srcNx + z1c * srcNx * srcNy;
        const idx101 = x1c + y0c * srcNx + z1c * srcNx * srcNy;
        const idx011 = x0c + y1c * srcNx + z1c * srcNx * srcNy;
        const idx111 = x1c + y1c * srcNx + z1c * srcNx * srcNy;

        const a = inputPixels[idx000];
        const b = inputPixels[idx100];
        const c = inputPixels[idx010];
        const d = inputPixels[idx110];
        const e = inputPixels[idx001];
        const f = inputPixels[idx101];
        const g = inputPixels[idx011];
        const h = inputPixels[idx111];

        if (fastAverage) {
            // super-cheap approximation
            return (a + b + c + d + e + f + g + h) / 8.0;
        }

        const tx = x - x0;
        const ty = y - y0;
        const tz = z - z0;

        const c00 = a * (1 - tx) + b * tx;
        const c10 = c * (1 - tx) + d * tx;
        const c01 = e * (1 - tx) + f * tx;
        const c11 = g * (1 - tx) + h * tx;

        const c0v = c00 * (1 - ty) + c10 * ty;
        const c1v = c01 * (1 - ty) + c11 * ty;

        return c0v * (1 - tz) + c1v * tz;
    };

    let outIndex = 0;

    const store = (value: number) => {
        let v = Math.round(value);
        if (v < clampMin) {
            v = clampMin;
        }
        if (v > clampMax) {
            v = clampMax;
        }
        outPixels[outIndex] = v;
    };

    let undefinedSamples = 0;

    if (options.patientSrcFromDstFn) {
        // Evaluated per voxel at voxel centers: +0.5 into world, −0.5 back to index.
        const mapPoint = options.patientSrcFromDstFn;
        const dstVoxelToWorld = dstGrid.voxelToWorld as mat4;
        const worldToSrcVoxel = mat4.create();
        if (!mat4.invert(worldToSrcVoxel, srcGrid.voxelToWorld as mat4)) {
            throw new Error('Source voxelToWorld transform is not invertible.');
        }
        const dstIndex = vec3.create();
        const dstWorld = vec3.create();
        const srcIndex = vec3.create();

        for (let k = 0; k < dstNz; k++) {
            for (let j = 0; j < dstNy; j++) {
                for (let i = 0; i < dstNx; i++, outIndex++) {
                    vec3.set(dstIndex, i + 0.5, j + 0.5, k + 0.5);
                    vec3.transformMat4(dstWorld, dstIndex, dstVoxelToWorld);
                    const srcWorld = mapPoint(dstWorld);
                    if (!srcWorld) {
                        undefinedSamples += 1;
                        outPixels[outIndex] = backgroundValue;
                        continue;
                    }
                    vec3.transformMat4(srcIndex, srcWorld, worldToSrcVoxel);
                    store(
                        sampleTrilinear(
                            srcIndex[0] - 0.5,
                            srcIndex[1] - 0.5,
                            srcIndex[2] - 0.5
                        )
                    );
                }
            }
        }

        return {
            pixels: outPixels,
            dstGrid,
            qaVoxelDstToSrc,
            undefinedSamples,
        };
    }

    for (let k = 0; k < dstNz; k++) {
        // baseZ = k * c2 + c3
        vec3.scale(baseZ, c2, k);
        vec3.add(baseZ, baseZ, c3);

        for (let j = 0; j < dstNy; j++) {
            // rowStart = baseZ + j * c1
            vec3.scale(rowStart, c1, j);
            vec3.add(rowStart, rowStart, baseZ);

            srcPos[0] = rowStart[0];
            srcPos[1] = rowStart[1];
            srcPos[2] = rowStart[2];

            for (let i = 0; i < dstNx; i++, outIndex++) {
                store(sampleTrilinear(srcPos[0], srcPos[1], srcPos[2]));

                // step one voxel in i
                srcPos[0] += c0[0];
                srcPos[1] += c0[1];
                srcPos[2] += c0[2];
            }
        }
    }

    return {
        pixels: outPixels,
        dstGrid,
        qaVoxelDstToSrc,
        undefinedSamples,
    };
}

export function resampleInt16Grid(
    srcPixels: Int16Array,
    srcGrid: Grid,
    dstGrid: Grid,
    options: ArbitraryResampleOptions = {}
): ResampleResult<Int16Array> {
    return resampleGridCPU(srcPixels, srcGrid, dstGrid, Int16Array, {
        backgroundValue: options.backgroundValue ?? 0,
        ...options,
        clampMin: options.clampMin ?? -32768,
        clampMax: options.clampMax ?? 32767,
    });
}

export function resampleUint8Grid(
    srcPixels: Uint8Array,
    srcGrid: Grid,
    dstGrid: Grid,
    options: ArbitraryResampleOptions = {}
): ResampleResult<Uint8Array> {
    return resampleGridCPU(srcPixels, srcGrid, dstGrid, Uint8Array, {
        backgroundValue: options.backgroundValue ?? 0,
        ...options,
        clampMin: options.clampMin ?? 0,
        clampMax: options.clampMax ?? 255,
    });
}
